import { useState } from "react";
import { Download, FileText, Link2, FolderOpen, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DownloadAssistantDialog } from "./DownloadAssistantDialog";
import { MergePDFDialog } from "./MergePDFDialog";
import { ShareDemarcheDialog } from "./ShareDemarcheDialog";
import { getExpiredDocuments } from "./ExpirationAlert";

interface LinkedDocument {
  id: string;
  label: string;
  doc_type: string | null;
  document_id: string;
  sort_order: number;
  document: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string;
    expires_at?: string | null;
  };
}

interface DemarcheExportBarProps {
  demarcheTitle: string;
  demarcheId: string;
  linkedDocuments: LinkedDocument[];
}

export function DemarcheExportBar({ 
  demarcheTitle, 
  demarcheId, 
  linkedDocuments 
}: DemarcheExportBarProps) {
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const docCount = linkedDocuments.length;
  const expiredDocs = getExpiredDocuments(linkedDocuments);
  const hasExpiredDocs = expiredDocs.length > 0;

  return (
    <>
      <Card className={`mb-6 ${hasExpiredDocs ? 'border-destructive/30 bg-destructive/5' : 'border-primary/20 bg-primary/5'}`}>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            {hasExpiredDocs ? (
              <AlertTriangle className="w-4 h-4 text-destructive" />
            ) : (
              <FolderOpen className="w-4 h-4 text-primary" />
            )}
            <span className="font-medium text-sm">Dossier prêt</span>
            <Badge variant="secondary" className="text-xs">
              {docCount} document{docCount > 1 ? 's' : ''}
            </Badge>
            {hasExpiredDocs && (
              <Badge variant="destructive" className="text-xs">
                {expiredDocs.length} expiré{expiredDocs.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDownloadDialogOpen(true)}
              className="flex-1 min-w-[120px]"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMergeDialogOpen(true)}
              className="flex-1 min-w-[120px]"
            >
              <FileText className="w-4 h-4 mr-2" />
              PDF unique
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShareDialogOpen(true)}
              className="flex-1 min-w-[120px]"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Partager
            </Button>
          </div>
        </div>
      </Card>

      <DownloadAssistantDialog
        open={downloadDialogOpen}
        onOpenChange={setDownloadDialogOpen}
        demarcheTitle={demarcheTitle}
        linkedDocuments={linkedDocuments}
      />

      <MergePDFDialog
        open={mergeDialogOpen}
        onOpenChange={setMergeDialogOpen}
        demarcheTitle={demarcheTitle}
        linkedDocuments={linkedDocuments}
      />

      <ShareDemarcheDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        demarcheTitle={demarcheTitle}
        demarcheId={demarcheId}
        linkedDocuments={linkedDocuments}
      />
    </>
  );
}
